import { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import useSessionManager from '../../hooks/useSessionManager';
import { fetchData } from '../../apiService';
import {
  Receipt, Payment, Warning, Schedule, LocalShipping,
  CheckCircle, Error, Info, Refresh, BarChart, Assessment,
  ShoppingCart, Inventory, KeyboardArrowDown, KeyboardArrowUp,
  TrendingUp, People, Store
} from '@mui/icons-material';
import './Home.css';

const Home = () => {
  const { validateSession } = useSessionManager();
  const { token, user, companyId, branchId } = useAuth();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    todayRevenue: 0, monthRevenue: 0, activeRentals: 0, availableEquipment: 0,
    pendingReturns: 0, overdueReturns: 0, totalClients: 0, totalVendors: 0,
    receivables: 0, payables: 0, cashBalance: 0, revenueGrowth: 0,
    equipmentUtilization: 0, rentInvoiceTotal: 0, rentInvoiceTotalAmount: 0,
    rentInvoiceBalanceAmount: 0, rentInvoicePaidAmount: 0
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [branchRevenueData, setBranchRevenueData] = useState([]);
  const [loadingBranchRevenue, setLoadingBranchRevenue] = useState(false);
  const [enquiryModalOpen, setEnquiryModalOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedDateData, setSelectedDateData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [expandedCustomers, setExpandedCustomers] = useState({});

  const [fromDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  });
  const [toDate] = useState(() => new Date().toISOString().split('T')[0]);

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toFixed(0)}`;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString('en-IN');
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    return { daysInMonth: lastDay.getDate(), startingDayOfWeek: firstDay.getDay(), year, month };
  };

  const getEventsForDate = (date) => {
    return calendarEvents.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.getDate() === date.getDate() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getFullYear() === date.getFullYear();
    });
  };

  const changeMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  const handleDateClick = async (date) => {
    if (!companyId || !token) return;
    setLoadingReport(true);
    setReportModalOpen(true);
    setExpandedCustomers({});
    try {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      const response = await fetchData('ReportApi', `/ForecastDatewise/${formattedDate}/${companyId}/${companyId}`, token);
      setSelectedDateData({ date: formattedDate, data: response.response_data || response || [] });
    } catch (error) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      setSelectedDateData({ date: `${year}-${month}-${day}`, data: [], error: 'Failed to load report data' });
    } finally {
      setLoadingReport(false);
    }
  };

  const toggleCustomer = (customerKey) => {
    setExpandedCustomers(prev => ({ ...prev, [customerKey]: !prev[customerKey] }));
  };

  const formatDateDDMMYYYY = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
  };

  const groupDataByCustomer = (data) => {
    if (!data || data.length === 0) return [];
    const grouped = data.reduce((acc, item) => {
      const key = `${item.CustomerName}_${item.ContactNumber1}_${item.AddressLine1}`;
      if (!acc[key]) acc[key] = { customerName: item.CustomerName, contactNumber: item.ContactNumber1, addressLine1: item.AddressLine1, addressLine2: item.AddressLine2, items: [] };
      acc[key].items.push(item);
      return acc;
    }, {});
    return Object.values(grouped);
  };

  const fetchBranchRevenue = useCallback(async () => {
    if (!token || !companyId) return;
    setLoadingBranchRevenue(true);
    try {
      const response = await fetchData('ReportApi', `/RevenueReport/${toDate}/${companyId}/0`, token);
      setBranchRevenueData(response.response_data || response || []);
    } catch (error) {
      setBranchRevenueData([]);
    } finally {
      setLoadingBranchRevenue(false);
    }
  }, [token, companyId, toDate]);

  const fetchDashboardData = useCallback(async () => {
    try {
      if (!token || !companyId) { setLoading(false); return; }
      fetchBranchRevenue();

      const [salesData, purchaseData, clientsData, vendorsData, itemsData, receiptsData,
        deliveryOutData, deliveryInData, estimateData, enquiryData, customerData, itemStockData, rentInvoiceData
      ] = await Promise.allSettled([
        fetchData('SaleApi', `/SalesList/${companyId}/${branchId || 0}/${fromDate}/${toDate}`, token).catch(() => ({ response_data: [] })),
        fetchData('PurchaseApi', `/PurchaseList/${companyId}/${branchId || 0}/${fromDate}/${toDate}`, token).catch(() => ({ response_data: [] })),
        fetchData('CustomerApi', `/${companyId}`, token).catch(() => ({ response_data: [] })),
        fetchData('VendorApi', `/${companyId}`, token).catch(() => ({ response_data: [] })),
        fetchData('ItemApi', `/${companyId}`, token).catch(() => ({ response_data: [] })),
        fetchData('PaymentReceiptsApi', `/PaymentReceiptsList/${companyId}/${branchId || 0}/${fromDate}/${toDate}`, token).catch(() => ({ response_data: [] })),
        fetchData('DeliveryCallApi', `/DeliveryOutList/${companyId}/${branchId || 0}/${fromDate}/${toDate}`, token).catch(() => ({ response_data: [] })),
        fetchData('DeliveryInApi', `/DeliveryInList/${companyId}/${branchId || 0}/${fromDate}/${toDate}`, token).catch(() => ({ response_data: [] })),
        fetchData('EstimateApi', `/EstimateList/${companyId}/${branchId || 0}/${fromDate}/${toDate}`, token).catch(() => ({ response_data: [] })),
        fetchData('EnquiryApi', `/EnquriyList/${companyId}/${branchId || 0}/${fromDate}/${toDate}`, token).catch(() => ({ response_data: [] })),
        fetchData('CustomerApi', `/${companyId}`, token).catch(() => ({ response_data: [] })),
        fetchData('SaleApi', `/ItemStockListDetails/${companyId}/${branchId || 0}`, token).catch(() => ({ response_data: [] })),
        fetchData('RentInvoiceApi', `/RentInvoiceList/${companyId}/${branchId || 0}/${fromDate}/${toDate}`, token).catch(() => ({ response_data: [] }))
      ]);

      const get = (result) => (result.status === 'fulfilled' ? result.value : { response_data: [] });
      const sales = get(salesData).response_data || get(salesData) || [];
      const purchases = get(purchaseData).response_data || get(purchaseData) || [];
      const clients = get(clientsData).response_data || get(clientsData) || [];
      const vendors = get(vendorsData).response_data || get(vendorsData) || [];
      const items = get(itemsData).response_data || get(itemsData) || [];
      const receipts = get(receiptsData).response_data || get(receiptsData) || [];
      const deliveryOut = get(deliveryOutData).response_data || [];
      const deliveryIn = get(deliveryInData).response_data || [];
      const estimates = get(estimateData).response_data || [];
      const enquiries = get(enquiryData).response_data || [];
      const rentInvoices = get(rentInvoiceData).response_data || [];

      setCustomers(get(customerData).response_data || []);
      setItems(get(itemStockData).response_data || []);

      const today = new Date().toDateString();
      const todayRevenue = sales.filter(s => new Date(s.SaleDate || s.BillDate).toDateString() === today).reduce((sum, s) => sum + (s.TotalAmount || 0), 0);
      const cm = new Date().getMonth(), cy = new Date().getFullYear();
      const monthRevenue = sales.filter(s => { const d = new Date(s.SaleDate || s.BillDate); return d.getMonth() === cm && d.getFullYear() === cy; }).reduce((sum, s) => sum + (s.TotalAmount || 0), 0);
      const totalSales = sales.reduce((sum, s) => sum + (s.TotalAmount || 0), 0);
      const receivables = sales.filter(s => s.PaymentStatus !== 'Paid').reduce((sum, s) => sum + ((s.TotalAmount || 0) - (s.PaidAmount || 0)), 0);
      const payables = purchases.filter(p => p.PaymentStatus !== 'Paid').reduce((sum, p) => sum + ((p.TotalAmount || 0) - (p.PaidAmount || 0)), 0);
      const totalReceipts = receipts.reduce((sum, r) => sum + (r.Amount || 0), 0);
      const totalPayments = purchases.reduce((sum, p) => sum + (p.PaidAmount || 0), 0);
      const cashBalance = totalReceipts - totalPayments;

      const deliveryOutPending = deliveryOut.filter(d => d.Status === 'Pending' || !d.Status).length;
      const deliveryInPending = deliveryIn.filter(d => d.Status === 'Pending' || !d.Status).length;
      const estimatesPending = estimates.filter(e => e.Status === 'Pending' || !e.Status).length;
      const estimatesApproved = estimates.filter(e => e.Status === 'Approved').length;
      const enquiriesPending = enquiries.filter(e => e.Status === 'Pending' || !e.Status).length;
      const enquiriesConverted = enquiries.filter(e => e.Status === 'Converted').length;

      const allActivities = [
        ...sales.slice(0, 5).map(s => ({ type: 'sale', title: `Sale to ${s.CustomerName || 'Client'}`, amount: s.TotalAmount || 0, date: new Date(s.SaleDate || s.BillDate).toLocaleDateString() })),
        ...deliveryOut.slice(0, 3).map(d => ({ type: 'delivery', title: `Delivery Out - ${d.CustomerName || 'Client'}`, amount: d.TotalAmount || 0, date: new Date(d.DeliveryDate || d.BillDate || d.CreatedDate).toLocaleDateString() })),
        ...estimates.slice(0, 3).map(e => ({ type: 'estimate', title: `Estimate - ${e.CustomerName || 'Client'}`, amount: e.TotalAmount || 0, date: new Date(e.EstimateDate || e.BillDate || e.CreatedDate).toLocaleDateString() }))
      ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 10);

      const newAlerts = [];
      const overdueReceivables = sales.filter(s => s.PaymentStatus !== 'Paid' && new Date(s.DueDate) < new Date());
      if (overdueReceivables.length > 0) newAlerts.push({ type: 'overdue', title: `${overdueReceivables.length} Overdue Receivables`, amount: overdueReceivables.reduce((sum, s) => sum + ((s.TotalAmount || 0) - (s.PaidAmount || 0)), 0), note: 'Immediate attention required' });
      if (deliveryOutPending > 0) newAlerts.push({ type: 'pending', title: `${deliveryOutPending} Pending Deliveries`, note: 'Awaiting delivery' });
      if (estimatesPending > 0) newAlerts.push({ type: 'pending', title: `${estimatesPending} Pending Estimates`, note: 'Awaiting approval' });
      const lowStock = items.filter(i => (i.CurrentStock || 0) < (i.MinStock || 10));
      if (lowStock.length > 0) newAlerts.push({ type: 'stock', title: `${lowStock.length} Items Low on Stock`, note: 'Reorder required' });

      setDashboardData({
        todayRevenue, monthRevenue, totalSales,
        deliveryOutTotal: deliveryOut.length, deliveryOutPending, deliveryOutCompleted: deliveryOut.filter(d => d.Status === 'Completed').length,
        deliveryInTotal: deliveryIn.length, deliveryInPending, deliveryInCompleted: deliveryIn.filter(d => d.Status === 'Completed').length,
        estimatesTotal: estimates.length, estimatesPending, estimatesApproved, estimatesRejected: estimates.filter(e => e.Status === 'Rejected').length,
        enquiriesTotal: enquiries.length, enquiriesPending, enquiriesConverted,
        totalClients: clients.length, totalVendors: vendors.length,
        receivables, payables, cashBalance, totalReceipts, totalPayments,
        availableEquipment: items.filter(i => (i.CurrentStock || 0) > 0).length,
        revenueGrowth: 12.5,
        equipmentUtilization: items.length > 0 ? ((items.filter(i => (i.CurrentStock || 0) > 0).length / items.length) * 100).toFixed(1) : 0,
        rentInvoiceTotal: rentInvoices.length,
        rentInvoiceTotalAmount: rentInvoices.reduce((sum, r) => sum + (r.TotalAmount || 0), 0),
        rentInvoicePaidAmount: rentInvoices.reduce((sum, r) => sum + (r.PaidAmount || 0), 0),
        rentInvoiceBalanceAmount: rentInvoices.reduce((sum, r) => sum + (r.BalanceAmount || 0), 0)
      });
      setRecentActivities(allActivities);
      setAlerts(newAlerts);
      setCalendarEvents(estimates.filter(e => e.EstimateDate || e.EventDate).map(e => ({
        id: e.Id, date: new Date(e.EstimateDate || e.EventDate),
        eventName: e.EventName || e.CustomerName || 'Event',
        address: e.Address || e.EventAddress || 'No address',
        customerName: e.CustomerName || 'Client', amount: e.TotalAmount || 0
      })));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, companyId, branchId, fromDate, toDate]);

  useEffect(() => {
    if (!validateSession()) { setLoading(false); return; }
    const t = setTimeout(() => setLoading(false), 3000);
    fetchDashboardData();
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="dash-loading">
        <Refresh className="dash-spin" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });

  const kpiCards = [
    { label: 'Delivery Out', value: formatNumber(dashboardData.deliveryOutTotal), icon: <LocalShipping />, color: 'cyan', route: '/DeliveryIndex' },
    { label: 'Delivery In', value: formatNumber(dashboardData.deliveryInTotal), icon: <LocalShipping />, color: 'violet', route: '/DeliveryInIndex' },
    { label: 'Rent Invoices', value: formatNumber(dashboardData.rentInvoiceTotal), sub: formatCurrency(dashboardData.rentInvoiceTotalAmount), icon: <Receipt />, color: 'amber', route: '/RentInvoiceIndex' },
    { label: 'Payment & Receipt', value: formatCurrency(dashboardData.cashBalance), sub: `Receipts: ${formatCurrency(dashboardData.totalReceipts)}`, icon: <Payment />, color: 'green', route: '/PaymentReceiptsIndex' },
    { label: 'Clients', value: formatNumber(dashboardData.totalClients), sub: `${dashboardData.totalVendors} vendors`, icon: <People />, color: 'indigo', route: '/clientIndex' },
  ];

  return (
    <div className="dash-root">

      {/* KPI Cards */}
      <div className="dash-kpi-grid">
        {kpiCards.map((card, i) => (
          <div key={i} className={`dash-kpi-card dash-kpi-${card.color}`} onClick={() => history.push(card.route)}>
            <div className={`dash-kpi-icon dash-icon-${card.color}`}>{card.icon}</div>
            <div className="dash-kpi-body">
              <span className="dash-kpi-label">{card.label}</span>
              <span className="dash-kpi-value">{card.value}</span>
              <span className="dash-kpi-sub">{card.sub}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Rent Invoice Summary Bar */}
      <div className="dash-rent-bar">
        <div className="dash-rent-item">
          <span className="dash-rent-label">Total Invoices</span>
          <span className="dash-rent-val">{dashboardData.rentInvoiceTotal}</span>
        </div>
        <div className="dash-rent-divider" />
        <div className="dash-rent-item">
          <span className="dash-rent-label">Total Amount</span>
          <span className="dash-rent-val">{formatCurrency(dashboardData.rentInvoiceTotalAmount)}</span>
        </div>
        <div className="dash-rent-divider" />
        <div className="dash-rent-item">
          <span className="dash-rent-label">Paid</span>
          <span className="dash-rent-val dash-rent-paid">{formatCurrency(dashboardData.rentInvoicePaidAmount)}</span>
        </div>
        <div className="dash-rent-divider" />
        <div className="dash-rent-item">
          <span className="dash-rent-label">Balance</span>
          <span className="dash-rent-val dash-rent-bal">{formatCurrency(dashboardData.rentInvoiceBalanceAmount)}</span>
        </div>
        <div className="dash-rent-divider" />
        <div className="dash-rent-item">
          <span className="dash-rent-label">Payables</span>
          <span className="dash-rent-val dash-rent-bal">{formatCurrency(dashboardData.payables)}</span>
        </div>
      </div>

      {/* Main 3-column layout */}
      <div className="dash-main-grid">

        {/* Left: Recent Activities */}
        <div className="dash-card">
          <div className="dash-card-header">
            <div className="dash-card-title"><BarChart className="dash-title-icon" /><span>Recent Activities</span></div>
            <button className="dash-link-btn" onClick={() => history.push('/SaleIndex')}>View All</button>
          </div>
          <div className="dash-activities">
            {recentActivities.length > 0 ? recentActivities.map((a, i) => (
              <div key={i} className="dash-activity-row">
                <div className={`dash-act-icon dash-act-${a.type}`}>
                  {a.type === 'sale' && <Receipt />}
                  {a.type === 'purchase' && <ShoppingCart />}
                  {a.type === 'payment' && <Payment />}
                  {a.type === 'delivery' && <LocalShipping />}
                  {a.type === 'estimate' && <Assessment />}
                </div>
                <div className="dash-act-info">
                  <span className="dash-act-title">{a.title}</span>
                  <span className="dash-act-amount">{formatCurrency(a.amount)}</span>
                </div>
                <span className="dash-act-date">{a.date}</span>
              </div>
            )) : (
              <div className="dash-empty"><Info /><p>No recent activities</p></div>
            )}
          </div>
        </div>

        {/* Center: Calendar */}
        <div className="dash-card">
          <div className="dash-card-header">
            <div className="dash-card-title"><span>📅 {monthName}</span></div>
            <div className="dash-cal-nav">
              <button className="dash-cal-btn" onClick={() => changeMonth(-1)}>‹</button>
              <button className="dash-cal-btn" onClick={() => changeMonth(1)}>›</button>
            </div>
          </div>
          <div className="dash-calendar">
            <div className="dash-cal-weekdays">
              {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => <span key={d} className="dash-cal-wd">{d}</span>)}
            </div>
            <div className="dash-cal-days">
              {Array.from({ length: startingDayOfWeek }).map((_, i) => <div key={`e-${i}`} className="dash-cal-day dash-cal-empty" />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const dayNum = i + 1;
                const dateObj = new Date(year, month, dayNum);
                const events = getEventsForDate(dateObj);
                const todayFlag = isToday(dateObj);
                return (
                  <div
                    key={dayNum}
                    className={`dash-cal-day${todayFlag ? ' dash-cal-today' : ''}${events.length > 0 ? ' dash-cal-has-event' : ''}`}
                    onClick={() => handleDateClick(dateObj)}
                    title={events.length > 0 ? events.map(e => e.eventName).join(', ') : ''}
                  >
                    <span className="dash-cal-num">{dayNum}</span>
                    {events.length > 0 && <span className="dash-cal-dot" />}
                  </div>
                );
              })}
            </div>
          </div>
          {calendarEvents.length > 0 && (
            <div className="dash-upcoming">
              <p className="dash-upcoming-title">Upcoming Events</p>
              {calendarEvents.slice(0, 3).map((ev, i) => (
                <div key={i} className="dash-event-row">
                  <div className="dash-event-badge">{new Date(ev.date).getDate()}</div>
                  <div className="dash-event-info">
                    <span className="dash-event-name">{ev.eventName}</span>
                    <span className="dash-event-loc">{ev.address}</span>
                  </div>
                  <span className="dash-event-amt">{formatCurrency(ev.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: Alerts */}
        <div className="dash-card">
          <div className="dash-card-header">
            <div className="dash-card-title"><Warning className="dash-title-icon dash-warn-icon" /><span>Alerts</span></div>
            <span className="dash-alert-badge">{alerts.length}</span>
          </div>
          <div className="dash-alerts">
            {alerts.length > 0 ? alerts.map((alert, i) => (
              <div key={i} className={`dash-alert-row dash-alert-${alert.type}`}>
                <div className="dash-alert-icon">
                  {alert.type === 'overdue' && <Error />}
                  {alert.type === 'pending' && <Schedule />}
                  {alert.type === 'stock' && <Inventory />}
                </div>
                <div className="dash-alert-body">
                  <span className="dash-alert-title">{alert.title}</span>
                  {alert.amount && <span className="dash-alert-amount">{formatCurrency(alert.amount)}</span>}
                  <span className="dash-alert-note">{alert.note}</span>
                </div>
              </div>
            )) : (
              <div className="dash-empty dash-empty-ok"><CheckCircle /><p>All clear! No alerts</p></div>
            )}
          </div>

          {/* Branch Revenue */}
          {branchRevenueData.length > 0 && (
            <div className="dash-branch-section">
              <div className="dash-branch-header">
                <Store className="dash-title-icon" />
                <span>Branch Revenue</span>
                <button className="dash-refresh-sm" onClick={fetchBranchRevenue} disabled={loadingBranchRevenue}>
                  <Refresh className={loadingBranchRevenue ? 'dash-spin' : ''} />
                </button>
              </div>
              <div className="dash-branch-list">
                {branchRevenueData.slice(0, 5).map((b, i) => (
                  <div key={i} className="dash-branch-row">
                    <span className="dash-branch-rank">{i + 1}</span>
                    <span className="dash-branch-name">{b.BranchName || b.branchName || `Branch ${i + 1}`}</span>
                    <span className="dash-branch-rev">{formatCurrency(b.Revenue || b.revenue || b.TotalRevenue || 0)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Date Report Modal */}
      {reportModalOpen && (
        <div className="modal-overlay" onClick={() => setReportModalOpen(false)}>
          <div className="modal-content report-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Forecast Report - {formatDateDDMMYYYY(selectedDateData?.date)}</h2>
              <button className="modal-close" onClick={() => setReportModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              {loadingReport ? (
                <div className="dash-loading"><Refresh className="dash-spin" /><p>Loading...</p></div>
              ) : selectedDateData?.error ? (
                <div className="error-state"><Error style={{ fontSize: '48px', color: '#ef4444' }} /><p>{selectedDateData.error}</p></div>
              ) : selectedDateData?.data?.length > 0 ? (
                <div className="report-data-grouped">
                  {groupDataByCustomer(selectedDateData.data).map((customer, index) => {
                    const key = `${customer.customerName}_${customer.contactNumber}_${customer.addressLine1}`;
                    const isExpanded = expandedCustomers[key];
                    return (
                      <div key={index} className="customer-group">
                        <div className="customer-header" onClick={() => toggleCustomer(key)} style={{ cursor: 'pointer' }}>
                          <div className="customer-info">
                            <div className="customer-name-row">
                              <span className="expand-icon">{isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}</span>
                              <div className="customer-details">
                                <div className="customer-name">{customer.customerName || 'N/A'}</div>
                                <div className="customer-contact">{customer.contactNumber || 'N/A'}</div>
                              </div>
                            </div>
                            <div className="customer-address">
                              {customer.addressLine1 && <div>{customer.addressLine1}</div>}
                              {customer.addressLine2 && <div className="address-line2">{customer.addressLine2}</div>}
                            </div>
                            <div className="items-count-badge">{customer.items.length} {customer.items.length === 1 ? 'Item' : 'Items'}</div>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="items-table-container">
                            <table className="items-table">
                              <thead><tr><th>Item Name</th><th>Forecast Qty</th><th>Delivery Date</th><th>Return Date</th><th>Expected Time</th></tr></thead>
                              <tbody>
                                {customer.items.map((item, ii) => (
                                  <tr key={ii}>
                                    <td className="item-name">{item.Name || 'N/A'}</td>
                                    <td className="qty-cell">{item.ForecastQty || 0}</td>
                                    <td>{formatDateDDMMYYYY(item.DeliveryDate)}</td>
                                    <td>{formatDateDDMMYYYY(item.DeliveryReturnDate)}</td>
                                    <td className="time-cell">{item.ExpectedTime || 'N/A'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state"><Info style={{ fontSize: '48px', color: '#d1d5db' }} /><p>No data available for this date</p></div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;